import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { WithdrawRequestModal, WithdrawRequestModalOptions } from './withdraw-request/withdraw-request.modal';
import { ViewXbrlOrTxtModal, ViewXbrlOrTxtModalOptions } from './view-xbrl/view-xbrl-or-txt.modal';
import { BaseDocumentDetailsModel, DocumentModel } from '../models/document.model';
import { ViewXmlModal, ViewXmlModalOptions } from './view-xbrl/view-xml.modal';
import { ToastService } from '../services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { CloudUploadDocumentModalOptions, CloudDocumentUploadModal } from './cloud-document/cloud-document-upload.modal';
import { CloudExportDocumentModalOptions, CloudDocumentExportModal } from './cloud-document/cloud-document-export.modal';
import { DocumentTypeEnum } from '../constants/document-type.enum';
import { CreditSummaryModel } from '../models/CreditSummary.model';
import { CreditSummaryExplanationModal, CreditSummaryExplanationModalOptions } from './credit-summary-explanation/credit-summary-explanation.modal';
import { Subscription } from 'rxjs';
import { StatusViewLogModal, StatusViewLogModalOptions } from './status-view-log/status-view-log.modal';
import { StatusEditSendgroupDetailsModal, StatusEditSendgroupDetailsOptions } from './status-edit-sendgroup-details/status-edit-sendgroup-details.modal';
import { DeleteRequestModal, DeleteRequestModalOptions } from './delete-request/delete-request.modal';
import { DocumentDetailsModal, DocumentDetailsModalOptions } from './document-details/document-details.modal';
import { BuilderRequestModel } from '../models/builder/builder-request.model';
import { Plugin } from '../models/plugin';
import { StatusRequestModel } from '../models/status/status-request.model';
import { StatusUpdateSendGroupModel } from '../models/status/status-sendgroup.model';
import { AuthenticationService } from '../services/authentication.service';
import { User } from '../models/user';
import { StatusEditRequestModal, StatusEditRequestOptions } from './status-edit-request/status-edit-request.modal';
import { EditFilingDataModalComponent, EditFilingDataModalOptions } from './edit-filing-data-modal/edit-filing-data-modal.component';
import { XbrlFilingData } from '../models/xbrlFilingData.model';
import { NewRequestPickerModal, NewRequestPickerModalOptions } from './new-request-picker/new-request-picker.modal';
import { CreateSupportTicketModal, CreateSupportTicketModalOptions } from './create-support-ticket/create-support-ticket.modal';

@Injectable({
    providedIn: 'root'
})
export class ModalOpenerService {

    modalSubscriptionShown: Subscription = new Subscription();
    modalSubscriptionClosed: Subscription = new Subscription();
    private currentUser!: User;
    private usersubscription: Subscription;

    constructor(private modalService: NgbModal, private toastService: ToastService, private translateService: TranslateService,
        private authenticationService: AuthenticationService

    ) {
        this.usersubscription = this.authenticationService.currentUser.subscribe((data) => {
            this.currentUser = data;
        });
    }

    ngOnDestroy(): void {
        if (this.modalSubscriptionShown) {
            this.modalSubscriptionShown.unsubscribe();
        }
        if (this.modalSubscriptionClosed) {
            this.modalSubscriptionClosed.unsubscribe();
        }
        if (this.usersubscription) {
            this.usersubscription.unsubscribe();
        }
    }

    /**
     * Opens the modal for viewing 'Xbrl'/'txt' file types
     * @param request
     * @param document
     * @param goToError
     * @returns
     */
    public ViewXblrOrTxt(request: StatusRequestModel | BuilderRequestModel, document: DocumentModel, goToError: boolean = false): void {
        // We don't want to open multiple modals
        if (this.modalService.hasOpenModals()) return;
        const modalRef = this.modalService.open(ViewXbrlOrTxtModal, ViewXbrlOrTxtModalOptions);
        modalRef.componentInstance.request = request;
        modalRef.componentInstance.documentId = document.id;
        modalRef.componentInstance.documentType = document.documentType;
        modalRef.componentInstance.goToError = goToError;
    }
    
    /**
     * Opens the modal for viewing 'Xml' file types
     * @param requestId
     * @param document
     * @returns
     */
    public ViewXml(requestId: string, document: DocumentModel): void {
        // We don't want to open multiple modals
        if (this.modalService.hasOpenModals()) return;
        if (document.documentType == DocumentTypeEnum.DetachedSignature || document.documentType == DocumentTypeEnum.AuditTrail) {
            // i cannot do this mate
            this.toastService.showWarning(this.translateService.instant("Toasts.InvalidFileFormatForViewingTitle"), this.translateService.instant("Toasts.InvalidFileFormatForViewingContent"));
            return;
        }
        const modalRef = this.modalService.open(ViewXmlModal, ViewXmlModalOptions);
        modalRef.componentInstance.requestId = requestId;
        modalRef.componentInstance.documentId = document.id;
        modalRef.componentInstance.documentType = document.documentType;
    }

    /**
     * Opens a modal for withdrawing the request(s)
     * - Returns a NgbModalRef for ComponentInstance: WithdrawRequestModal
     */
    public WithDrawnRequest(requestIds: string[]): NgbModalRef {
        const modalRef = this.modalService.open(WithdrawRequestModal, WithdrawRequestModalOptions);
        (modalRef.componentInstance as WithdrawRequestModal).selectedRequestIds = requestIds;
        return modalRef;
    }

    /**
     * Opens a modal for deleting the request(s)
     * - Returns a NgbModalRef for ComponentInstance: WithdrawRequestModal
     */
    public DeleteRequest(requestIds: string[]): NgbModalRef {
        const modalRef = this.modalService.open(DeleteRequestModal, DeleteRequestModalOptions);
        (modalRef.componentInstance as DeleteRequestModal).selectedRequestIds = requestIds;
        return modalRef;
    }

    /**
     * Opens a modal displaying the total credit cost explanation for the given summary
     */
    public CreditSummaryExplanation(creditSummary: CreditSummaryModel): void {
        // We don't want to open multiple modals
        if (this.modalService.hasOpenModals()) return;
        const modalRef = this.modalService.open(CreditSummaryExplanationModal, CreditSummaryExplanationModalOptions);
        modalRef.componentInstance.creditSummary = creditSummary;

    }

    /**
     * Opens a modal allowing document selection for the given request from the cloud
     */
    public CloudDocumentUpload(requestId: string, supportedDocumentTypes: string[] | undefined, allowMultiselect: boolean = true): NgbModalRef {
        const modalRef = this.modalService.open(CloudDocumentUploadModal, CloudUploadDocumentModalOptions);
        (modalRef.componentInstance as CloudDocumentUploadModal).modalTitleTranslationKey = "CloudLocations.UploadTitle";
        (modalRef.componentInstance as CloudDocumentUploadModal).enabledPlugins = this.currentUser.enabledOrganisationPlugins;
        (modalRef.componentInstance as CloudDocumentUploadModal).acceptedDocumentTypes = supportedDocumentTypes;
        (modalRef.componentInstance as CloudDocumentUploadModal).allowMultiselect = allowMultiselect;
        return modalRef;
    }

    /**
     * Opens a modal allowing folder selection from the cloud
     * -CloudDocumentExportModal
     */
    public CloudFolderPicker(requestId: string, request: StatusRequestModel | BuilderRequestModel, fileName?: string): NgbModalRef {
        var modalRef = this.CloudFolderPickerForSpecific(requestId, this.currentUser.enabledOrganisationPlugins, request, fileName);
        return modalRef;
    }
    /**
     * Opens a modal allowing folder selection from the cloud
     * -CloudDocumentExportModal
     */
    public CloudFolderPickerForSpecific(requestId: string, desiredPlugins: Plugin[], request: BuilderRequestModel | StatusRequestModel, fileName?: string,): NgbModalRef {
        const modalRef = this.modalService.open(CloudDocumentExportModal, CloudExportDocumentModalOptions);
        (modalRef.componentInstance as CloudDocumentExportModal).modalTitleTranslationKey = "CloudLocations.SelectFolderTitle";
        (modalRef.componentInstance as CloudDocumentExportModal).requestId = requestId;
        // (modalRef.componentInstance as CloudDocumentExportModal).requestName = requestName;
        (modalRef.componentInstance as CloudDocumentExportModal).enabledPlugins = desiredPlugins;
        (modalRef.componentInstance as CloudDocumentExportModal).isForAutoExport = true;
        (modalRef.componentInstance as CloudDocumentExportModal).fileName = fileName || "";
        (modalRef.componentInstance as CloudDocumentExportModal).request = request || "";

        return modalRef;
    }
    /**
     * Opens a modal allowing document EXPORT to the cloud
     */
    public CloudDocumentExport(requestId?: string, fileName?: string, exportRequest: boolean = true): NgbModalRef {
        const modalRef = this.modalService.open(CloudDocumentExportModal, CloudExportDocumentModalOptions);
        (modalRef.componentInstance as CloudDocumentExportModal).modalTitleTranslationKey = exportRequest ? "CloudLocations.ExportRequestTitle" : "CloudLocations.ExportDocumentTitle";
        modalRef.componentInstance.enabledPlugins = this.currentUser.enabledOrganisationPlugins;
        (modalRef.componentInstance as CloudDocumentExportModal).requestId = requestId!;
        (modalRef.componentInstance as CloudDocumentExportModal).fileName = fileName || "";
        modalRef.componentInstance.allowMultiselect = false;
        return modalRef;
    }
    /**
     * Opens a modal linked to the Status service for data, showing the 'EventLog'
     * @returns
     */
    public StatusViewLog(): NgbModalRef {
        const modalRef = this.modalService.open(StatusViewLogModal, StatusViewLogModalOptions);
        return modalRef;
    }

    public EditRequest(request: StatusRequestModel): NgbModalRef {
        // We don't want to open multiple modals
        if (this.modalService.hasOpenModals()) return null as any;
        const modalRef = this.modalService.open(StatusEditRequestModal, StatusEditRequestOptions);
        modalRef.componentInstance.request = request;
        return modalRef;
    }

    /**
     * Opens a modal that can Edit the given sendgroup (Name)
     * @param sendgroupToUpdate
     * @returns
     */
    public StatusEditSendgroupDetails(sendgroupToUpdate: StatusUpdateSendGroupModel): NgbModalRef {
        const modalRef = this.modalService.open(StatusEditSendgroupDetailsModal, StatusEditSendgroupDetailsOptions);
        modalRef.componentInstance.sendgroupToUpdate = sendgroupToUpdate;
        return modalRef;
    }

    /**
     * Shows the document details for the given Data
     * @param document
     * @returns
     */
    public DocumentDetails(document: BaseDocumentDetailsModel): void {
        // We don't want to open multiple modals
        if (this.modalService.hasOpenModals()) return;
        const modalRef = this.modalService.open(DocumentDetailsModal, DocumentDetailsModalOptions);
        modalRef.componentInstance.document = document;
        modalRef.componentInstance.documentId = document.id;
        modalRef.componentInstance.documentType = document.documentType;
    }

    /**
     * Opens a modal to edit filing data
     * @param filingData
     * @param onSaved called after a successful save, since saving decides whether the request is still held back
     * @returns
     */
    public EditFilingData(requestId: string, filingData: XbrlFilingData, onSaved?: () => void): void {
        // We don't want to open multiple modals
        if (this.modalService.hasOpenModals()) return;
        const modalRef = this.modalService.open(EditFilingDataModalComponent, EditFilingDataModalOptions);
        modalRef.componentInstance.requestId = requestId;
        modalRef.componentInstance.filingData = filingData;
        modalRef.componentInstance.closeModal.subscribe((savedFilingData: XbrlFilingData | null) => {
            modalRef.close();
            if (savedFilingData && onSaved) {
                onSaved();
            }
        });
    }

    public openNewRequestPickerModal(request: BuilderRequestModel | null = null, newRequestMode: boolean = true): void {
        // We don't want to open multiple modals
        if (this.modalService.hasOpenModals()) return;
        const modalRef = this.modalService.open(NewRequestPickerModal, NewRequestPickerModalOptions);
        modalRef.componentInstance.newRequestMode = newRequestMode;
        if(request) {
            modalRef.componentInstance.request = request;
        }
    }

    public OpenCreateSupportTicketModal(): void {
        // We don't want to open multiple modals
        if (this.modalService.hasOpenModals()) return;
        const modalRef = this.modalService.open(CreateSupportTicketModal, CreateSupportTicketModalOptions);
    }
}
